import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { WidgetDataResult, WidgetDataRow, WidgetChartConfig, WidgetClickPoint } from '../types';
import { mergeEChartsOption, formatNumber, escapeHtml, ECHARTS_TOOLTIP_STYLE } from './echartsUtils';
import {
  MAP_SENTIMENT_COLORS,
  SENTIMENT_ORDER,
  normalizeSentiment,
  formatSentimentLabel,
} from './sentimentColors';
import { getWorldGeoJson } from './worldMapGeoJson';
import { observeResize } from '../observeResize';
import {
  loadRegionalMap,
  getRegionName,
  getRegionalCities,
  getRegionalCoreBounds,
  LoadedRegionalMap,
  RegionalMapKey,
  GEO_ASPECT_SCALE,
  expandBoundsToAspect,
} from './regionalMapGeoJson';

// ECharts zooms map/geo by a fixed 10–40% per wheel *event*, no matter how
// small the delta is. Trackpads and high-resolution mice emit dozens of events
// per gesture, so one flick can zoom 10x+. We intercept the wheel ourselves and
// scale proportionally to the scrolled distance instead. Raise to zoom faster.
const WHEEL_ZOOM_SENSITIVITY = 0.001;
// Trackpad pinch arrives as ctrl+wheel with tiny deltas — boost it so a pinch
// feels comparable to a scroll.
const PINCH_ZOOM_BOOST = 4;
// Per-event steps still compound over a hard flick (dozens of events plus
// trackpad momentum), so total zoom is rate-limited with a token bucket in
// log-zoom space: at most MAX_ZOOM_PER_SECOND x per second of scrolling, with
// a small instant burst so a single notch still responds immediately.
const MAX_ZOOM_PER_SECOND = 2.5;
const ZOOM_BURST_FACTOR = 1.3;
const ZOOM_RATE_PER_MS = Math.log(MAX_ZOOM_PER_SECOND) / 1000;
const ZOOM_BUDGET_MAX = Math.log(ZOOM_BURST_FACTOR);

let registeredGeoJson: object | null = null;
function ensureWorldMapRegistered() {
  const geoJson = getWorldGeoJson();
  if (registeredGeoJson !== geoJson) {
    echarts.registerMap('world', geoJson as any);
    registeredGeoJson = geoJson;
  }
}

interface ChartMapProps {
  data: WidgetDataResult;
  config: WidgetChartConfig;
  onPointClick?: (point: WidgetClickPoint) => void;
}

export const ChartMap: React.FC<ChartMapProps> = ({ data, config, onPointClick }) => {
  const { t } = useTranslation();
  const mapConfig = config.mapConfig ?? {};
  const mapType = mapConfig.mapType ?? 'world';
  const isRegional = mapType !== 'world';

  if (!isRegional) ensureWorldMapRegistered();

  const regionalKey = isRegional ? (mapType as RegionalMapKey) : null;
  // Russia-only variant options; inert for other maps.
  const federalLevel = mapConfig.federalLevel === 'districts' ? 'districts' : 'subjects';
  const showKaliningrad = mapConfig.showKaliningrad ?? true;
  const variantKey = regionalKey
    ? regionalKey === 'russia'
      ? `russia|${federalLevel}|${showKaliningrad ? 'kgd' : 'nokgd'}`
      : regionalKey
    : null;
  const [loadedRegionalMap, setLoadedRegionalMap] = useState<{
    key: string;
    map: LoadedRegionalMap;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Only expose a loaded map once it matches the active type + variant, so a
  // stale map from the previous config can't render against the new
  // bounds/cities/data.
  const regionalMap =
    variantKey && loadedRegionalMap?.key === variantKey ? loadedRegionalMap.map : null;
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReactECharts>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);

  // Regional maps size their bounding box to the widget's aspect ratio, so the
  // map fills the container instead of being letterboxed by ECharts' fit.
  useEffect(() => {
    if (!isRegional) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      setContainerSize((prev) =>
        prev && Math.abs(prev.w - w) < 2 && Math.abs(prev.h - h) < 2 ? prev : { w, h }
      );
    };
    update();
    return observeResize(el, update);
  }, [isRegional]);

  useEffect(() => {
    // Clear any prior error on every transition — including back to the world
    // map, which would otherwise stay hidden behind a stale regional load error.
    setLoadError(null);
    if (!regionalKey || !variantKey) return undefined;
    let cancelled = false;
    loadRegionalMap(regionalKey, { federalLevel, excludeKaliningrad: !showKaliningrad })
      .then((m) => {
        if (!cancelled) setLoadedRegionalMap({ key: variantKey, map: m });
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [regionalKey, variantKey, federalLevel, showKaliningrad]);

  const countryCol = mapConfig.countryColumn || data.columns[0];
  const valueCol = mapConfig.valueColumn;
  const regionMapping = mapConfig.regionMapping;
  const tooltipCols = mapConfig.tooltipColumns ?? data.columns.filter((c) => c !== countryCol);
  const tooltipLabels = mapConfig.tooltipLabels;
  const colorLow = mapConfig.colorRange?.[0] ?? '#dbeafe';
  const colorHigh = mapConfig.colorRange?.[1] ?? '#1d4ed8';
  const emptyColor = mapConfig.emptyColor ?? '#e5e7eb';
  const roam = mapConfig.roam ?? true;
  const showRank = mapConfig.showRank ?? false;
  const seaColor = mapConfig.seaColor ?? '#e9f0f7';
  const center = mapConfig.center;
  const zoom = mapConfig.zoom;
  // Labels and sentiment fills bind via geo.regions on regional maps and via
  // per-item label/itemStyle on the world map's plain map series.
  const labelColumn = mapConfig.labelColumn;
  const sentimentColumn = mapConfig.sentimentColumn;
  // City names clash with on-region labels, so labelColumn flips the default
  // to hidden; an explicit showCities: true still wins.
  const showCities = mapConfig.showCities ?? !labelColumn;
  const showSentimentLegend = mapConfig.showSentimentLegend ?? true;

  // Replace ECharts' built-in wheel zoom (see WHEEL_ZOOM_SENSITIVITY). The
  // listener runs in the capture phase and stops propagation so zrender's own
  // wheel handler never fires; drag-to-pan and touch pinch stay with ECharts.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !roam) return undefined;
    let budget = ZOOM_BUDGET_MAX;
    let lastTs = performance.now();
    const onWheel = (e: WheelEvent) => {
      const chart = chartRef.current?.getEchartsInstance();
      if (!chart) return;
      e.preventDefault();
      e.stopPropagation();
      // Normalize to pixels: deltaMode 1 = lines, 2 = pages.
      let delta = e.deltaY;
      if (e.deltaMode === 1) delta *= 16;
      else if (e.deltaMode === 2) delta *= el.clientHeight || 400;
      if (e.ctrlKey) delta *= PINCH_ZOOM_BOOST;
      if (delta === 0) return;
      // Refill the budget for time elapsed, then spend as much of this event's
      // (log-space) step as the budget allows. Scroll up (negative) zooms in.
      const now = performance.now();
      budget = Math.min(ZOOM_BUDGET_MAX, budget + (now - lastTs) * ZOOM_RATE_PER_MS);
      lastTs = now;
      const wanted = -delta * WHEEL_ZOOM_SENSITIVITY;
      const step = Math.sign(wanted) * Math.min(Math.abs(wanted), budget);
      if (step === 0) return;
      budget -= Math.abs(step);
      const zoom = Math.exp(step);
      const rect = chart.getDom().getBoundingClientRect();
      chart.dispatchAction({
        type: 'geoRoam',
        ...(isRegional
          ? { componentType: 'geo', geoIndex: 0 }
          : { componentType: 'series', seriesIndex: 0 }),
        zoom,
        originX: e.clientX - rect.left,
        originY: e.clientY - rect.top,
      });
    };
    el.addEventListener('wheel', onWheel, { capture: true, passive: false });
    return () => el.removeEventListener('wheel', onWheel, { capture: true });
  }, [roam, isRegional]);

  // Rows indexed by (optionally regionMapping-translated) code. When several
  // raw values map to the same code, numeric columns are summed.
  const rowByCode = useMemo(() => {
    const index: Record<string, WidgetDataRow> = {};
    for (const row of data.rows) {
      const raw = String(row[countryCol] ?? '').trim();
      if (!raw) continue;
      const code = (regionMapping?.[raw] ?? raw).toUpperCase();
      const prev = index[code];
      if (!prev) {
        index[code] = { ...row };
        continue;
      }
      for (const col of Object.keys(row)) {
        const a = prev[col];
        const b = row[col];
        if (typeof a === 'number' && typeof b === 'number') prev[col] = a + b;
      }
    }
    return index;
  }, [data.rows, countryCol, regionMapping]);

  const seriesData = useMemo(() => {
    // On regional maps only join rows to known region codes, so stray values
    // (e.g. country codes) can't paint the silent context backdrop.
    const joinable = regionalMap
      ? Object.keys(rowByCode).filter((code) => regionalMap.regionCodes.includes(code))
      : Object.keys(rowByCode);
    const entries: Array<Record<string, any>> = joinable.map((code) =>
      valueCol
        ? { name: code, value: Number(rowByCode[code][valueCol]) || 0 }
        : { name: code, value: 1, itemStyle: { areaColor: colorHigh } }
    );
    // On regional maps, add dataless regions so they stay hoverable with a
    // name-only tooltip.
    if (regionalMap) {
      const have = new Set(joinable);
      for (const code of regionalMap.regionCodes) {
        if (!have.has(code)) entries.push({ name: code });
      }
    }
    return entries;
  }, [rowByCode, valueCol, colorHigh, regionalMap]);

  const rankByCode = useMemo(() => {
    if (!valueCol) return null;
    const valued = seriesData.filter((d) => typeof d.value === 'number');
    const sorted = [...valued].sort((a, b) => (b.value as number) - (a.value as number));
    const rank: Record<string, number> = {};
    sorted.forEach((d, i) => {
      rank[d.name] = i + 1;
    });
    return { rank, total: sorted.length };
  }, [seriesData, valueCol]);

  const tooltipFormatter = useMemo(() => {
    return (params: any) => {
      const code = String(params.name ?? '');
      const row = rowByCode[code];
      if (!row) return `<b>${escapeHtml(getRegionName(code))}</b>`;

      const parts: string[] = [`<b>${escapeHtml(getRegionName(code))}</b>`];
      if (showRank && rankByCode) {
        const r = rankByCode.rank[code];
        if (r != null) parts.push(`<span style="color:#9ca3af;font-size:11px">#${r} of ${rankByCode.total}</span>`);
      }
      for (const col of tooltipCols) {
        const label = escapeHtml(tooltipLabels?.[col] ?? col.replace(/_/g, ' '));
        const val = row[col];
        // The sentiment column shows its human-readable form ('mostly_negative' → 'Mostly negative')
        const formatted =
          typeof val === 'number'
            ? formatNumber(val)
            : escapeHtml(
                sentimentColumn && col === sentimentColumn
                  ? t(formatSentimentLabel(val))
                  : String(val ?? '—')
              );
        parts.push(`${label}: <b>${formatted}</b>`);
      }
      return parts.join('<br/>');
    };
  }, [rowByCode, tooltipCols, tooltipLabels, rankByCode, showRank, sentimentColumn, t]);

  // Neighbor countries as a silent gray backdrop, plus per-region overrides
  // for sentiment fills and on-region value labels.
  const geoRegions = useMemo(() => {
    if (!regionalMap) return [];
    const mute = { emphasis: { disabled: true }, select: { disabled: true }, label: { show: false } };
    const regions: Array<Record<string, any>> = regionalMap.contextCodes.map((code) => ({
      name: code,
      silent: true,
      itemStyle: { areaColor: '#f3f4f6', borderColor: '#d5dbe1', borderWidth: 0.5 },
      ...mute,
    }));
    if (!sentimentColumn && !labelColumn) return regions;
    for (const code of regionalMap.regionCodes) {
      const row = rowByCode[code];
      if (!row) continue;
      const entry: Record<string, any> = { name: code };
      if (sentimentColumn) {
        const sentiment = normalizeSentiment(row[sentimentColumn]);
        if (sentiment) {
          entry.itemStyle = { areaColor: MAP_SENTIMENT_COLORS[sentiment].fill };
          // Darker sentiment color on hover instead of the generic blue
          entry.emphasis = { itemStyle: { areaColor: MAP_SENTIMENT_COLORS[sentiment].hover } };
        }
      }
      if (labelColumn) {
        const raw = row[labelColumn];
        if (raw !== null && raw !== undefined && String(raw).trim() !== '') {
          const text = typeof raw === 'number' ? formatNumber(raw) : String(raw);
          const labelStyle = {
            show: true,
            formatter: () => text,
            fontSize: 10,
            color: '#1f2937',
            textBorderColor: '#ffffff',
            textBorderWidth: 2,
          };
          entry.label = labelStyle;
          entry.emphasis = { ...(entry.emphasis ?? {}), label: labelStyle };
        }
      }
      if (Object.keys(entry).length > 1) regions.push(entry);
    }
    return regions;
  }, [regionalMap, rowByCode, sentimentColumn, labelColumn]);

  const option = useMemo(() => {
    const numericValues = valueCol
      ? seriesData.map((d) => d.value).filter((v): v is number => typeof v === 'number')
      : [];
    const minVal = numericValues.length ? Math.min(...numericValues) : 0;
    const maxVal = numericValues.length ? Math.max(...numericValues) : 1;
    // Sentiment fills replace the choropleth: no visualMap legend, no flat
    // presence fill — region colors come from geo.regions (see geoRegions).
    const sentimentActive = Boolean(sentimentColumn);
    // ECharts' continuous visualMap degenerates at min === max, painting every shape (incl. neighbors/unmatched) one color.
    const isFlatRange = Boolean(valueCol) && numericValues.length > 0 && minVal === maxVal;
    const needsFlatFill = (!valueCol || isFlatRange) && !sentimentActive;
    const matchedCodes = needsFlatFill
      ? seriesData.filter((d) => typeof d.value === 'number').map((d) => d.name as string)
      : [];
    // Per-item itemStyle/label only render on world maps; regional maps bind via geoIndex, which ignores them (see geo.regions below).
    let resolvedSeriesData = sentimentActive
      ? seriesData.map((d) => {
          const sentiment = normalizeSentiment(rowByCode[d.name]?.[sentimentColumn!]);
          // Strip the presence-mode itemStyle so unrecognized values fall back to emptyColor
          if (!sentiment) return { name: d.name, value: d.value };
          return {
            name: d.name,
            value: d.value,
            itemStyle: { areaColor: MAP_SENTIMENT_COLORS[sentiment].fill },
            emphasis: { itemStyle: { areaColor: MAP_SENTIMENT_COLORS[sentiment].hover } },
          };
        })
      : needsFlatFill
        ? seriesData.map((d) =>
            typeof d.value === 'number' ? { ...d, itemStyle: { areaColor: colorHigh } } : d
          )
        : seriesData;
    if (!isRegional && labelColumn) {
      resolvedSeriesData = resolvedSeriesData.map((d) => {
        const raw = rowByCode[d.name]?.[labelColumn];
        if (raw === null || raw === undefined || String(raw).trim() === '') return d;
        const text = typeof raw === 'number' ? formatNumber(raw) : String(raw);
        const labelStyle = {
          show: true,
          formatter: () => text,
          fontSize: 10,
          color: '#1f2937',
          textBorderColor: '#ffffff',
          textBorderWidth: 2,
        };
        return { ...d, label: labelStyle, emphasis: { ...(d.emphasis ?? {}), label: labelStyle } };
      });
    }

    const itemStyle = {
      areaColor: emptyColor,
      borderColor: '#c8d4e0',
      borderWidth: 0.5,
    };
    const emphasis = {
      label: { show: false },
      itemStyle: {
        areaColor: '#60a5fa',
        borderColor: '#3b82f6',
        borderWidth: 1,
      },
    };
    const base: Record<string, any> = {
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        formatter: tooltipFormatter,
        ...ECHARTS_TOOLTIP_STYLE,
      },
    };

    if (isRegional && regionalMap) {
      const coreBounds = getRegionalCoreBounds(mapType as RegionalMapKey, {
        excludeKaliningrad: !showKaliningrad,
      });
      base.backgroundColor = seaColor;
      base.geo = {
        map: regionalMap.mapName,
        roam,
        aspectScale: GEO_ASPECT_SCALE,
        ...(center ? { center } : {}),
        ...(zoom != null ? { zoom } : {}),
        boundingCoords: containerSize
          ? expandBoundsToAspect(coreBounds, containerSize.w, containerSize.h)
          : coreBounds,
        itemStyle,
        emphasis,
        // No persistent selected state on click — hover emphasis only
        selectedMode: false,
        // Merge flat-fill colors into existing per-region entries (labels) by
        // name — duplicate names in geo.regions would drop one of the entries.
        regions: needsFlatFill
          ? (() => {
              const merged = geoRegions.map((r) => ({ ...r }));
              for (const code of matchedCodes) {
                const existing = merged.find((r) => r.name === code && !r.silent);
                if (existing) existing.itemStyle = { ...existing.itemStyle, areaColor: colorHigh };
                else merged.push({ name: code, itemStyle: { areaColor: colorHigh } });
              }
              return merged;
            })()
          : geoRegions,
      };
      base.series = [
        {
          type: 'map',
          map: regionalMap.mapName,
          geoIndex: 0,
          data: resolvedSeriesData,
          nameProperty: 'name',
          // No persistent selected state on click — hover emphasis only.
          // Without this the series falls back to ECharts' default select
          // style (gold fill + region-name label) on click.
          selectedMode: false,
        },
      ];
      if (showCities) {
        base.series.push({
          type: 'scatter',
          coordinateSystem: 'geo',
          silent: true,
          z: 4,
          symbolSize: 4,
          itemStyle: { color: '#6b7280', borderColor: '#ffffff', borderWidth: 1 },
          label: {
            show: true,
            position: 'right',
            distance: 4,
            fontSize: 10,
            color: '#4b5563',
            textBorderColor: '#ffffff',
            textBorderWidth: 2,
            formatter: (p: any) => p.name,
          },
          // Shift colliding labels apart instead of hiding them, so every
          // region capital stays visible at any widget size
          labelLayout: { moveOverlap: 'shiftY' },
          tooltip: { show: false },
          data: getRegionalCities(mapType as RegionalMapKey)
            // Hide the Kaliningrad marker when the exclave itself is hidden
            .filter((c) => showKaliningrad || c.name !== 'Kaliningrad')
            .map((c) => ({
              name: c.name,
              value: c.coord,
            })),
        });
      }
    } else {
      base.series = [
        {
          type: 'map',
          map: 'world',
          roam,
          ...(center ? { center } : {}),
          ...(zoom != null ? { zoom } : {}),
          // Clip to avoid Arctic/Antarctic rendering artifacts in equirectangular projection
          boundingCoords: [[-180, 83], [180, -58]],
          data: resolvedSeriesData,
          nameProperty: 'name',
          itemStyle,
          emphasis,
          // No persistent selected state on click — hover emphasis only
          selectedMode: false,
        },
      ];
    }

    if (valueCol && numericValues.length && !isFlatRange && !sentimentActive) {
      base.visualMap = {
        type: 'continuous',
        seriesIndex: 0,
        min: minVal,
        max: maxVal,
        left: 8,
        bottom: 8,
        itemWidth: 14,
        itemHeight: 80,
        calculable: false,
        inRange: { color: [colorLow, colorHigh] },
        text: [formatNumber(maxVal), formatNumber(minVal)],
        textStyle: { fontSize: 11, color: '#2a324b' },
        backgroundColor: 'rgba(255,255,255,0.88)',
        borderColor: '#e1e5ee',
        borderWidth: 1,
        padding: [8, 10],
      };
    }

    return mergeEChartsOption(base, config.echartsOption);
  }, [
    seriesData,
    tooltipFormatter,
    roam,
    center,
    zoom,
    emptyColor,
    valueCol,
    colorLow,
    colorHigh,
    isRegional,
    regionalMap,
    mapType,
    showKaliningrad,
    sentimentColumn,
    labelColumn,
    rowByCode,
    geoRegions,
    showCities,
    seaColor,
    containerSize,
    config.echartsOption,
  ]);

  const onEvents = useMemo(() => {
    if (!onPointClick) return undefined;
    return {
      click: (params: any) => {
        if (params.seriesType === 'scatter') return;
        const code = String(params.name ?? '');
        const row = rowByCode[code];
        onPointClick({
          label: code,
          key: code,
          value: (typeof params.value === 'number' ? params.value : row?.[valueCol ?? '']) ?? 0,
          extra: row
            ? ({ ...row } as Record<string, string | number | boolean | null>)
            : undefined,
        });
      },
    };
  }, [onPointClick, rowByCode, valueCol]);

  // Sentiments present in the joined data, in canonical order — the legend
  // occupies the corner the choropleth visualMap uses in gradient mode.
  const legendSentiments = useMemo(() => {
    if (!sentimentColumn || !showSentimentLegend) return [];
    if (isRegional && !regionalMap) return [];
    const codes = regionalMap ? regionalMap.regionCodes : Object.keys(rowByCode);
    const present = new Set(
      codes
        .map((code) => normalizeSentiment(rowByCode[code]?.[sentimentColumn]))
        .filter((s): s is NonNullable<typeof s> => s !== null)
    );
    return SENTIMENT_ORDER.filter((s) => present.has(s));
  }, [sentimentColumn, showSentimentLegend, isRegional, regionalMap, rowByCode]);

  // Keep the container mounted across states so the ResizeObserver stays live
  // and load errors are scoped to regional maps (never blocking the world map).
  const regionalReady = regionalMap && containerSize;
  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      {loadError && isRegional ? (
        <div role="alert" style={{ padding: '1rem', fontSize: '0.8125rem', color: '#6b7280' }}>
          {loadError}
        </div>
      ) : (
        (!isRegional || regionalReady) && (
          <>
            <ReactECharts
              ref={chartRef}
              option={option}
              style={{ width: '100%', height: '100%' }}
              opts={{ renderer: 'svg' }}
              onEvents={onEvents}
              notMerge
            />
            {legendSentiments.length > 0 && (
              <ul
                aria-label={t('Sentiment legend')}
                style={{
                  position: 'absolute',
                  left: '0.5rem',
                  bottom: '0.5rem',
                  margin: 0,
                  padding: '0.5rem 0.625rem',
                  listStyle: 'none',
                  background: 'rgba(255,255,255,0.88)',
                  border: '1px solid #e1e5ee',
                  borderRadius: '0.375rem',
                  fontSize: '0.6875rem',
                  color: '#2a324b',
                  pointerEvents: 'none',
                }}
              >
                {legendSentiments.map((sentiment) => (
                  <li
                    key={sentiment}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', lineHeight: 1.6 }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: '0.625rem',
                        height: '0.625rem',
                        borderRadius: '0.125rem',
                        background: MAP_SENTIMENT_COLORS[sentiment].fill,
                        border: '1px solid rgba(0,0,0,0.12)',
                        flexShrink: 0,
                      }}
                    />
                    {t(formatSentimentLabel(sentiment))}
                  </li>
                ))}
              </ul>
            )}
          </>
        )
      )}
    </div>
  );
};
